import React, { useState } from 'react';
import { startCase } from 'lodash';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import { Link, useRouteMatch } from 'react-router-dom';

import { Button } from 'components/ui/button';
import { Avatar } from '@buildoutinc/blueprint-react/ui/Avatar';
import { Badge } from '@buildoutinc/blueprint-react/ui/Badge';
import { Dialog } from '@buildoutinc/blueprint-react/ui/Dialog';
import { Offcanvas } from '@buildoutinc/blueprint-react/ui/Offcanvas';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark, faTrash, faUser } from '@fortawesome/pro-regular-svg-icons';

import DateWithHover from 'components/timestamps/date_with_hover';
import { ACCESS_LEVELS, publicAccessDisplay } from 'bundles/leads/helper/lead_access_levels';
import AccessLevelToShow from 'bundles/leads/index/table/cells/document_progress/access_level_to_show';

export interface InquiryPanelLead {
  id: number;
  accountStatus?: string | null;
  contactCompany?: string | null;
  contactPhoneNumber?: string | null;
  createdAt?: string | null;
  docAccessLevelForLease?: string | null;
  docAccessLevelForSale?: string | null;
  emailAddress?: string | null;
  expirationDate1031?: string | null;
  is1031Profile?: boolean | null;
  leadData: { initials?: string | null; jobTitle?: string | null; name?: string | null };
  leaseVaultGatesAccessLevelWithoutOverride?: string | null;
  listingLabel?: string | null;
  oneTimeLinkSentAt?: string | null;
  profileUserType?: string | null;
  referralSourceIcon?: string | null;
  referralSourceLabel?: string | null;
  saleVaultGatesAccessLevelWithoutOverride?: string | null;
  source?: string | null;
  statusLabel?: string | null;
  updatedAt?: string | null;
  useVaultGates?: boolean;
  user?: { name?: string | null } | null;
}

interface Props {
  canDisplayEmailLink: boolean;
  deleting: boolean;
  lead: InquiryPanelLead | null;
  leaseChecked: boolean;
  saleChecked: boolean;
  showSpace: boolean;
  onClose: () => void;
  onDelete: (lead: InquiryPanelLead) => void;
}

const blank = <span className="text-muted">—</span>;

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="d-flex align-items-center justify-content-between gap-3 border-bottom py-2">
      <span className="text-muted text-nowrap">{label}</span>
      <span className="fw-semibold text-end" style={{ minWidth: 0 }}>{value || blank}</span>
    </div>
  );
}

function Section({ children, title }: { children: React.ReactNode; title: string }) {
  return (
    <div>
      <div className="fw-semibold fs-large mb-1">{title}</div>
      {children}
    </div>
  );
}

function date(value?: string | null) {
  return value ? <DateWithHover timestamp={value} /> : null;
}

function initials(lead: InquiryPanelLead) {
  if (lead.leadData.initials) return lead.leadData.initials;

  const words = (lead.leadData.name || '').split(/\s+/).filter(Boolean);

  return words.slice(0, 2).map(word => word[0].toUpperCase()).join('');
}

function roleJobTitle(lead: InquiryPanelLead) {
  return [startCase(lead.profileUserType || ''), startCase(lead.leadData.jobTitle || '')]
    .filter(part => part.trim().length > 0)
    .join(' / ');
}

function addedBy(lead: InquiryPanelLead) {
  if (lead.referralSourceIcon) return lead.referralSourceLabel;
  if (lead.source === 'connect') return 'Connect';
  if (lead.source === 'web_activity') return 'Web Activity';

  return lead.user?.name;
}

function docAccessLevel(lead: InquiryPanelLead, side: 'Lease' | 'Sale') {
  const value = lead[`docAccessLevelFor${side}`];

  if (lead.useVaultGates) {
    const isPinned = value !== 'no_override';
    const withoutOverride = side === 'Sale'
      ? lead.saleVaultGatesAccessLevelWithoutOverride
      : lead.leaseVaultGatesAccessLevelWithoutOverride;
    const valueToShow = isPinned ? value : withoutOverride;
    if (!valueToShow || valueToShow === 'no_override') return null;

    return (
      <AccessLevelToShow
        isPinned={isPinned}
        uniqueId={`inquiry-panel-${side}-${lead.id}`}
        valueToShow={valueToShow}
      />
    );
  }

  return ACCESS_LEVELS.find(level => level.value === value)?.label || publicAccessDisplay;
}

export default function InquiryPanel({
  canDisplayEmailLink, deleting, lead, leaseChecked, saleChecked, showSpace, onClose, onDelete
}: Props) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const { url } = useRouteMatch();

  if (!lead) return null;

  const name = lead.leadData.name;
  const role = roleJobTitle(lead);
  const verified = lead.accountStatus === 'Verified';
  const phone = lead.contactPhoneNumber
    ? (formatPhoneNumberIntl(lead.contactPhoneNumber) || lead.contactPhoneNumber)
    : null;
  const exchange1031 = typeof lead.is1031Profile === 'boolean' ? (lead.is1031Profile ? 'Yes' : 'No') : null;

  const handleOpenChange = (open: boolean) => {
    if (!open) onClose();
  };

  const handleDelete = () => {
    setConfirmingDelete(false);
    onDelete(lead);
  };

  return (
    <>
      <Offcanvas open onOpenChange={handleOpenChange}>
        <Offcanvas.Content aria-label={`${name} inquiry`} side="right" style={{ width: 'min(32rem, 100vw)' }}>
          <Offcanvas.Header className="d-flex flex-column align-items-stretch gap-3">
            <div className="d-flex align-items-center gap-3">
              <Avatar size="lg">
                <Avatar.Fallback className="fw-semibold">{initials(lead)}</Avatar.Fallback>
              </Avatar>
              <div style={{ minWidth: 0 }}>
                <Offcanvas.Title className="fs-4 fw-semibold mb-0 text-truncate">{name}</Offcanvas.Title>
                <div className="text-muted text-truncate">
                  {[role, lead.contactCompany].filter(Boolean).join(' · ') || '—'}
                </div>
              </div>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Badge
                appearance="muted"
                variant={!lead.statusLabel || lead.statusLabel === 'No Status' ? 'secondary' : 'primary'}
              >
                {lead.statusLabel || 'No Status'}
              </Badge>
              {lead.accountStatus && (
                <Badge appearance="muted" variant="secondary">
                  {verified ? 'Verified' : <><FontAwesomeIcon icon={faCircleXmark} />Not Verified</>}
                </Badge>
              )}
              {showSpace && lead.listingLabel && (
                <Badge appearance="muted" variant="secondary">{lead.listingLabel}</Badge>
              )}
            </div>
          </Offcanvas.Header>

          <Offcanvas.Body className="d-flex flex-column gap-4">
            <Section title="Contact">
              <Row
                label="Email"
                value={lead.emailAddress && (canDisplayEmailLink
                  ? <a href={`mailto:${lead.emailAddress}`}>{lead.emailAddress}</a>
                  : lead.emailAddress)}
              />
              <Row label="Phone" value={phone && <a href={`tel:${lead.contactPhoneNumber}`}>{phone}</a>} />
              <Row label="Company" value={lead.contactCompany} />
              <Row label="Role / Job Title" value={role} />
            </Section>

            <Section title="Inquiry">
              <Row label="Inquiry Status" value={lead.statusLabel} />
              <Row label="Referral Source" value={lead.referralSourceLabel} />
              <Row label="Link Sent" value={date(lead.oneTimeLinkSentAt)} />
              <Row label="Added By" value={addedBy(lead)} />
              <Row label="Date Added" value={date(lead.createdAt)} />
              <Row label="Last Updated" value={date(lead.updatedAt)} />
            </Section>

            <Section title="Access">
              {saleChecked && <Row label="Sale Doc Access Level" value={docAccessLevel(lead, 'Sale')} />}
              {leaseChecked && <Row label="Lease Doc Access Level" value={docAccessLevel(lead, 'Lease')} />}
              <Row label="Account Status" value={lead.accountStatus} />
            </Section>

            <Section title="1031 Exchange">
              <Row label="1031 Exchange" value={exchange1031} />
              <Row label="1031 Expiration" value={date(lead.expirationDate1031)} />
            </Section>
          </Offcanvas.Body>

          <Offcanvas.Footer className="d-flex justify-content-between gap-2">
            <Offcanvas.Close render={<Button variant="ghost">Close</Button>} />
            <div className="d-flex gap-2">
              <Button
                disabled={deleting}
                icon={faTrash}
                label="Delete Inquiry"
                variant="destructive"
                onClick={() => setConfirmingDelete(true)}
              />
              <Button
                icon={faUser}
                label="View Contact"
                render={<Link to={`${url}/${lead.id}`} />}
                variant="primary"
              />
            </div>
          </Offcanvas.Footer>
        </Offcanvas.Content>
      </Offcanvas>

      <Dialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
        <Dialog.Content>
          <Dialog.Header>
            <Dialog.Title>Delete this inquiry?</Dialog.Title>
          </Dialog.Header>
          <Dialog.Body>
            <p className="mb-0">{name} will be removed from this deal&rsquo;s inquiries.</p>
            <p className="text-muted mb-0 mt-2">
              The contact record stays in your book of business — only the inquiry is deleted.
            </p>
          </Dialog.Body>
          <Dialog.Footer>
            <Dialog.Cancel>Cancel</Dialog.Cancel>
            <Button icon={faTrash} label="Delete Inquiry" variant="destructive" onClick={handleDelete} />
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </>
  );
}
